import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/upr-api";
import { ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);

  const safeBody = useMemo(() => DOMPurify.sanitize(post?.body || "", {
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur"],
    // KEEP_CONTENT defaults to true — leaving unset preserves paragraph text.
  }), [post?.body]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="px-6 md:px-12 lg:px-16 py-24" data-testid="blog-not-found">
        <Link to="/blog" className="upr-link-underline text-sm">← Back to Blog</Link>
        <h1 className="font-heading font-black text-5xl mt-8">Post not found.</h1>
      </div>
    );
  }
  if (!post) return <div className="px-6 md:px-12 lg:px-16 py-24" data-testid="blog-loading">Loading…</div>;

  return (
    <article className="px-6 md:px-12 lg:px-16 py-12 md:py-20" data-testid="blog-detail">
      <Link to="/blog" className="inline-flex items-center gap-1 upr-link-underline text-sm" data-testid="back-to-blog">
        <ArrowLeft size={14} /> Back to Blog
      </Link>
      <div className="upr-eyebrow mt-8 mb-4">
        {new Date(post.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" })} · {post.author}
      </div>
      <h1 className="font-heading font-black tracking-tighter leading-[0.88] text-5xl md:text-7xl max-w-4xl" style={{ letterSpacing: "-0.035em" }} data-testid="post-title">
        {post.title}
      </h1>
      {post.cover_url && (
        <div className="mt-10 upr-cover" style={{ border: "1px solid var(--upr-border)", maxHeight: "70vh", overflow: "hidden" }}>
          <img src={post.cover_url} alt={post.title} className="w-full max-h-[70vh] object-cover" />
        </div>
      )}
      <div
        className="mt-10 max-w-3xl font-body text-base md:text-lg leading-relaxed upr-prose"
        data-testid="post-body"
        dangerouslySetInnerHTML={{ __html: safeBody }}
      />
    </article>
  );
}
